import { getHostname } from '@gls-its-frontends/common/gls-app-config';
import { glsReportCustomDynatraceError } from '@gls-its-frontends/common/gls-dynatrace';
import { ENVIRONMENT_HOSTNAMES } from '@/constants/environment-hostnames';
import { Environment } from '@/enums/environment';
import { generateChecksum } from '@/helpers/generate-checksum';
import { GetDeliveryStoresResponse, GetDeliveryStoresResponseItem, StoreAddress } from '@/types/api/store-management';
import { ShopAddress, ShopAddressesFile } from '@/types/shop-address';

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
class StaticStoresService {
    public static get checksum(): Promise<string> {
        return generateChecksum(this._stores);
    }

    private static readonly _stores: ShopAddress[] = [];

    public static async init(): Promise<void> {
        const isProd: boolean = getHostname() === ENVIRONMENT_HOSTNAMES[Environment.PROD];

        try {
            const file: Response = await fetch('/shops.json');
            const content: ShopAddressesFile = await file.json();

            if (!isProd) {
                const testStoresFile: Response = await fetch('/test-shops.json');
                const testStoresContent: ShopAddressesFile = await testStoresFile.json();

                content.shops.push(...testStoresContent.shops);
            }

            this._stores.push(...content.shops);
        } catch (error: unknown) {
            console.error('Unable to fetch stores: ', error);
        }
    }

    public static getPickupStore(contactId: string): StoreAddress | null {
        const store: ShopAddress | undefined = this._stores.find((item: ShopAddress): boolean => item.contactId === contactId);

        if (!store) {
            glsReportCustomDynatraceError('static pickup store not found', contactId);

            return null;
        }

        return this.asGetStoreAddressApiResponse(store as ShopAddress);
    }

    public static getDeliveryStoreDetails(contactId: string): StoreAddress {
        const store: ShopAddress | undefined = this._stores.find((item: ShopAddress): boolean => item.contactId === contactId);

        if (!store) {
            glsReportCustomDynatraceError('static delivery store not found', contactId);

            throw new Error(`static store not found, ${contactId}`);
        }

        return this.asGetStoreAddressApiResponse(store as ShopAddress);
    }

    public static getDeliveryStores(countryCode: string, pickupContactId: string): GetDeliveryStoresResponse {
        const items: GetDeliveryStoresResponseItem[] = this._stores
            .filter((item: ShopAddress): boolean => item.contactId !== pickupContactId && item.countryCode === countryCode)
            .map(this.asGetDeliveryStoresApiResponseItem);

        return {
            stores: items,
            page: 0,
            pageSize: items.length,
            totalItems: items.length,
            totalPages: 1
        };
    }

    private static asGetStoreAddressApiResponse(store: ShopAddress): StoreAddress {
        return {
            contactId: store.contactId,
            storeCode: store.storeCode,
            name1: store.storeName,
            name2: store.storeName2 || undefined,
            city: store.city,
            street: store.street,
            streetNo: store.houseNumber || undefined,
            zipCode: store.zipCode,
            countryCode: store.countryCode,
            phone: store.phone,
            email: store.email,
            contactPerson: store.customerId // TODO: missing in static data,
        };
    }

    private static asGetDeliveryStoresApiResponseItem(store: ShopAddress): GetDeliveryStoresResponseItem {
        return {
            contactId: store.contactId,
            name1: store.storeName,
            storeCode: store.storeCode
        };
    }
}

export { StaticStoresService };
